import React, { useRef, useCallback } from 'react';

const COLUMNS = [
  { key: 'ticker', label: 'Ticker',       placeholder: 'AAPL',       type: 'text',   hint: 'Required' },
  { key: 'type',   label: 'Type',         placeholder: 'Purchase',   type: 'select', hint: 'Optional' },
  { key: 'date',   label: 'Date',         placeholder: 'MM/DD/YYYY', type: 'date',   hint: 'Optional' },
  { key: 'amount', label: 'Amount (USD)', placeholder: '50000.00',   type: 'text',   hint: 'Optional' },
];

const TYPE_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sale',     label: 'Sale' },
];

function newRow(id) {
  return { id, ticker: '', type: '', date: '', amount: '' };
}

function toInputDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const isoPrefix = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoPrefix) {
    return `${isoPrefix[1]}-${isoPrefix[2].padStart(2, '0')}-${isoPrefix[3].padStart(2, '0')}`;
  }

  const us = raw.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{2,4})$/);
  if (us) {
    let y = us[3];
    if (y.length === 2) y = `20${y}`;
    return `${y}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }

  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return '';
}

export default function SpreadsheetEditor({ rows, onChange }) {
  const tableRef = useRef(null);

  const updateCell = useCallback((rowId, key, value) => {
    onChange(rows.map(r => r.id === rowId ? { ...r, [key]: value } : r));
  }, [rows, onChange]);

  const deleteRow = useCallback((rowId) => {
    const next = rows.filter(r => r.id !== rowId);
    onChange(next.length ? next : [newRow(Date.now())]);
  }, [rows, onChange]);

  const addRow = useCallback(() => {
    onChange([...rows, newRow(Date.now() + Math.random())]);
  }, [rows, onChange]);

  // Keyboard navigation: Tab → next cell, Enter → new row
  const handleKeyDown = useCallback((e, rowIdx, colIdx) => {
    const colCount = COLUMNS.length;
    const rowCount = rows.length;

    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = colIdx + (e.shiftKey ? -1 : 1);
      const nextRow = rowIdx + (nextCol >= colCount ? 1 : nextCol < 0 ? -1 : 0);
      const actualCol = ((nextCol % colCount) + colCount) % colCount;
      const actualRow = Math.max(0, Math.min(rowCount - 1, nextRow));
      focusCell(actualRow, actualCol);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx === rowCount - 1) {
        addRow();
        setTimeout(() => focusCell(rowIdx + 1, 0), 30);
      } else {
        focusCell(rowIdx + 1, 0);
      }
    }
  }, [rows, addRow]);

  const focusCell = (rowIdx, colIdx) => {
    const table = tableRef.current;
    if (!table) return;
    const trs = table.querySelectorAll('tbody tr');
    if (!trs[rowIdx]) return;
    const cells = trs[rowIdx].querySelectorAll('input, select');
    if (cells[colIdx]) cells[colIdx].focus();
  };

  return (
    <div className="stonks-sheet-wrap">
      <div className="stonks-sheet-table-wrap">
        <table className="stonks-sheet-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="stonks-sheet-row-num-th">#</th>
              {COLUMNS.map(c => (
                <th key={c.key} className={`stonks-sheet-${c.key}-th`}>
                  {c.label}
                  <span style={{ color: 'var(--s-text-muted)', marginLeft: 4, fontSize: 9, fontWeight: 400 }}>
                    {c.hint}
                  </span>
                </th>
              ))}
              <th className="stonks-sheet-del-th" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={row.id}>
                <td>
                  <span className="stonks-row-num">{rowIdx + 1}</span>
                </td>

                {/* Ticker */}
                <td>
                  <input
                    className="stonks-cell-input"
                    value={row.ticker}
                    placeholder="AAPL"
                    onChange={e => updateCell(row.id, 'ticker', e.target.value.toUpperCase())}
                    onKeyDown={e => handleKeyDown(e, rowIdx, 0)}
                    style={{ fontFamily: 'var(--s-mono)', fontWeight: 600, letterSpacing: '0.3px' }}
                    spellCheck={false}
                    autoCapitalize="characters"
                  />
                </td>

                {/* Type */}
                <td>
                  <select
                    className="stonks-cell-select"
                    value={row.type}
                    onChange={e => updateCell(row.id, 'type', e.target.value)}
                    onKeyDown={e => handleKeyDown(e, rowIdx, 1)}
                    style={{ color: row.type === 'purchase' ? 'var(--s-green)' : row.type === 'sale' ? 'var(--s-red)' : 'var(--s-text-dim)' }}
                  >
                    <option value="">Purchase</option>
                    {TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>

                {/* Date */}
                <td>
                  <input
                    className="stonks-cell-input"
                    type="date"
                    value={row.date}
                    onChange={e => updateCell(row.id, 'date', e.target.value)}
                    onKeyDown={e => handleKeyDown(e, rowIdx, 2)}
                    style={{ fontFamily: 'var(--s-mono)', colorScheme: 'dark' }}
                  />
                </td>

                {/* Amount */}
                <td>
                  <input
                    className="stonks-cell-input"
                    value={row.amount}
                    placeholder="e.g. $50,000 or 50000.00"
                    onChange={e => updateCell(row.id, 'amount', e.target.value)}
                    onKeyDown={e => handleKeyDown(e, rowIdx, 3)}
                    style={{ fontFamily: 'var(--s-mono)' }}
                  />
                </td>

                {/* Delete */}
                <td>
                  <button
                    className="stonks-cell-delete"
                    onClick={() => deleteRow(row.id)}
                    title="Delete row"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button className="stonks-sheet-add-row" onClick={addRow}>
          <span>＋</span> Add row
        </button>
      </div>
    </div>
  );
}

// Helper to convert raw rows (from DB or import) to editor rows
export function toEditorRows(rawRows) {
  if (!rawRows?.length) return [newRow(1)];
  return rawRows.map((r, i) => ({
    id: r.id || i + 1,
    ticker: r.ticker || '',
    type: r.type || '',
    date: toInputDate(r.date),
    amount: r.amount || '',
  }));
}

export function fromEditorRows(editorRows) {
  return editorRows.filter(r => r.ticker.trim());
}
